import React, { useState, useEffect, useRef } from "react";
import { useHistory } from "react-router-dom";
import {
  Box,
  Flex,
  Heading,
  Text,
  Button,
  Image,
  Stack,
  Link,
  SimpleGrid,
  useColorMode,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Input,
  InputGroup,
  IconButton,
  InputRightElement,
  VStack,
  useDisclosure,
  HStack,
  useBreakpointValue,
} from "@chakra-ui/react";
import { FaLocationArrow } from "react-icons/fa";
import { Link as RouterLink } from "react-router-dom";
import adventure from "../assets/images/Route66.jpg";
import HomeToGo from "../assets/images/HomeToGo.png";
import Viaiator from "../assets/images/logo-viator-facebook.png";
import Aviasales from "../assets/images/Aviasales_logo_horizontal.png";
import Outdoorsy from "../assets/images/outdoorsy.png";
import Wise from "../assets/images/wise.png";
import expedia from "../assets/images/expedia-logo-black-and-white.png";
import SkyScanner from "../assets/images/Skyscanner_Logo.png";
import HeroImage from "../assets/images/hero_image.png";
import logo from "../assets/images/Newlogo-black.png";
import { useScroll } from "../components/ScrollContext";
import { CookieConsentPopup, NewsletterSubscriptionPopup } from "./Popups"; // Import the popups
import { useAuth } from "../auth";
import { useGoogleAuth } from "./common/google/GoogleAuthContext";
import TripSection from "./TripSection";
import Footer from "./Footer";
import VideoSection from "./VideoSection";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import SlickSlider from "react-slick";
import Navbar from "../components/Navbar";
import BackToTopButton from "../components/common/BackToTopButton";
import Translate from "../components/LanguageTranslation/Translate";
import { useTranslation } from "../components/LanguageTranslation/TranslationContext";
import { motion, AnimatePresence } from "framer-motion";
import { keyframes } from "@emotion/react";
import JoinCommunitySection from "./common/JoinCommunitySection";
import { Helmet } from "react-helmet-async";

const MotionBox = motion(Box);

// Define the blinking keyframes animation
const blinkAnimation = keyframes`
  0% { opacity: 1; }
  50% { opacity: 0; }
  100% { opacity: 1; }
`;

////////////////////////////////////
// 1) Child: Handles typing logic //
////////////////////////////////////
function TypingSuggestion({ text, onFinishedTyping }) {
  const { useTranslate } = useTranslation();
  const translatedText = useTranslate(text); // ⬅️ Get translated full string
  const [charIndex, setCharIndex] = useState(0);

  useEffect(() => {
    if (charIndex < translatedText.length) {
      const timeout = setTimeout(() => {
        setCharIndex((prev) => prev + 1);
      }, 70);
      return () => clearTimeout(timeout);
    } else {
      onFinishedTyping();
    }
  }, [charIndex, translatedText, onFinishedTyping]);

  return (
    <MotionBox
      initial={{ y: "100%", opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      exit={{ y: "-100%", opacity: 0 }}
      transition={{ duration: 0.4 }}
    >
      {translatedText.slice(0, charIndex)}
    </MotionBox>
  );
}

//////////////////////////////////////////
// 2) Parent: Manages swipe + cycling  //
//////////////////////////////////////////
const suggestions = [
  "INSPIRE WHERE TO GO",
  "FIND LODGING",
  "FIND ACTIVITIES",
  "BUILD AN ITINERARY",
];

const HomePage = () => {
  const { colorMode } = useColorMode();
  const { partnersRef } = useScroll();
  const history = useHistory();
  const [showCookiePopup, setShowCookiePopup] = useState(false);
  const [logged] = useAuth();
  const { authState } = useGoogleAuth();

  const {
    isOpen: isNewsletterOpen,
    onOpen: onOpenNewsletter,
    onClose: onCloseNewsletter,
  } = useDisclosure();



  useEffect(() => {
    const cookiePopupShown = localStorage.getItem("cookiePopupShown");
    const newsletterPopupShown = localStorage.getItem("newsletterPopupShown");
    const isUserLoggedIn = logged || authState.isLoggedIn;

    if (!cookiePopupShown) {
      setTimeout(() => {
        setShowCookiePopup(true); // Show cookie popup after 1 second
      }, 1000);
    }

    // Show newsletter popup if:
    // 1. Newsletter popup hasn't been shown yet AND
    // 2. Either user is logged in OR (cookie popup was shown and accepted OR cookie popup is disabled)
    if (!newsletterPopupShown && (isUserLoggedIn || cookiePopupShown || !cookiePopupShown)) {
      // Show newsletter subscription popup after 1 second
      setTimeout(() => {
        onOpenNewsletter();
      }, 1000);
    }
  }, [onOpenNewsletter, logged, authState.isLoggedIn]);

  const handleCookieAccept = () => {
    localStorage.setItem("cookiePopupShown", "true");
    setShowCookiePopup(false);

    // Show the newsletter popup 1 second after accepting cookies (if not already shown)
    if (!localStorage.getItem("newsletterPopupShown")) {
      setTimeout(() => {
        onOpenNewsletter();
      }, 1000);
    }
  };

  const handleNewsletterClose = () => {
    localStorage.setItem("newsletterPopupShown", "true");
    onCloseNewsletter();
  };

  const [userMessage, setUserMessage] = useState("");

  const handleSubmit = (e) => {
    e.preventDefault();
    localStorage.removeItem("conversation_id");

    if (!userMessage.trim()) {
      // If userMessage is empty or only spaces, redirect to /ai-travel-planner
      history.push("/ai-travel-planner");
    } else {
      // Otherwise, redirect with the message
      history.push(
        `/ai-travel-planner/chat?message=${encodeURIComponent(userMessage)}`
      );
    }
  };

  const handleSuggestionClick = (message) => {
    localStorage.removeItem("conversation_id");
    setUserMessage(message);
    history.push(
      `/ai-travel-planner/chat?message=${encodeURIComponent(message)}`
    );
  };

  const [currentIndex, setCurrentIndex] = useState(0);
  const [isExiting, setIsExiting] = useState(false);
  const [cycle, setCycle] = useState(0);
  const [inputFocused, setInputFocused] = useState(false);
  const [inputValue, setInputValue] = useState("");

  const shouldShowSuggestions = !inputFocused && inputValue.trim() === "";

  const handleFinishedTyping = () => {
    setTimeout(() => {
      setIsExiting(true);
    }, 1000);
  };

  const handleExitComplete = () => {
    setIsExiting(false);
    setCurrentIndex((prev) => (prev + 1) % suggestions.length);
  };

  const handleLinkClick = (message) => {
    localStorage.removeItem("conversation_id"); // Clear any existing conversation
    history.push(
      `/ai-travel-planner/chat?message=${encodeURIComponent(message)}`
    ); // Redirect to the chat system with the clicked text
  };

  const handleFocus = () => {
    setInputFocused(true);
    setIsExiting(false);
  };

  const handleBlur = () => {
    setInputFocused(false);
    setIsExiting(false);
    setCycle((c) => c + 1); // triggers new animation if needed
  };

  return (
    <>
      <Helmet>
        <title>
          NOMAD360: AI Travel Planner for Your Outdoor Adventures. Personalized
          Itineraries for Hiking, Camping, Backpacking and Family Trips.
        </title>
        <meta
          name="description"
          content="Plan your next outdoor adventure with NOMAD360, your AI travel planner. Get smart, personalized itineraries for hiking, camping, backpacking, and family trips — tailored to you."
        />
        <meta
          name="description:mobile"
          content="Plan your next outdoor adventure with NOMAD360. Smart, personalized itineraries for hiking, camping, backpacking, and more."
        />
      </Helmet>

      <Box bg={colorMode === "light" ? "white" : "gray.800"}>
        <Box
          backgroundColor="#061019"
          position="relative"
          // zIndex="0"
          p={0}
          m={0}
        >
          {/* Navbar */}
          <Box position="absolute" top={0} left={0} width="100%" zIndex="2">
            <Navbar
              bgColor="transparent" // Make the background of the navbar transparent
              linkColor="white"
              styling="unstyled"
              logoSrc={logo}
              languageCodeColor="white"
              userProfileBgColor="blackAlpha.300"
            />
          </Box>

          {/* Hero Section */}
          <Box
            position="relative"
            zIndex="1"
            height={{ base: "auto", md: "100vh" }}
            display="flex"
            justifyContent="flex-start"
            alignItems="center"
            backgroundColor="white"
            p={0}
            m={0}
          >
            {/* Background Image */}
            <Box
              width="100%"
              height="100%"
              backgroundImage={`url(${HeroImage})`}
              backgroundSize="cover"
              backgroundPosition="center"
              backgroundRepeat="no-repeat"
              h="100vh"
              position="relative"
              borderRadius="0"
              zIndex="1"
            >
              {/* Content Section */}
              <Flex
                direction="column"
                w="100%"
                h="100vh"
                position="relative"
                align="center" // Horizontally center children
                justify="center" // Vertically center children
                backgroundImage={`url(${HeroImage})`}
                backgroundSize="cover"
                backgroundPosition="center"
                backgroundRepeat="no-repeat"
              >
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  justifyContent="center"
                  textAlign="center"
                  w="100%"
                  gap={6} // spacing between heading and input
                >
                  <Heading
                    as="h1"
                    fontSize={{ base: "4xl", md: "5xl", lg: "7xl", xl: "9xl" }}
                    fontFamily="Poppins, sans-serif"
                    color="white"
                    textShadow="1px 1px 2px rgba(0, 0, 0, 0.5)"
                    w={{ base: "100%", md: "80%", lg: "60%", xl: "60%" }}
                  >
                    <Translate text="YOUR ADVENTURE, PERFECTLY PLANNED" />
                  </Heading>

                  <InputGroup
                    size={{ base: "lg", md: "xl", lg: "xl" }}
                    w={{ base: "90%", md: "75%", lg: "60%" }}
                    position="relative"
                  >
                    <Input
                      placeholder=""
                      value={inputValue}
                      onChange={(e) => {
                        setUserMessage(e.target.value);
                        setInputValue(e.target.value);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && !e.shiftKey) {
                          e.preventDefault();
                          handleSubmit(e); // just pass the event
                        }
                      }}
                      onFocus={handleFocus}
                      onBlur={handleBlur}
                      bg="blackAlpha.300"
                      color="white"
                      border="4px solid #FFC719"
                      borderRadius="sm"
                      pr="7rem"
                      pl={4}
                      h={{ base: "60px", md: "70px", lg: "88px", xl: "110px" }}
                      fontSize={{ base: "lg", md: "xl", lg: "2xl", xl: "3xl" }}
                      _hover={{ borderColor: "#FFC719" }}
                      _focus={{ borderColor: "#FFC719" }}
                    />

                    {/* Suggestion overlay only when input not focused */}
                    {!inputFocused && shouldShowSuggestions && (
                      <Box
                        position="absolute"
                        top={0}
                        left={0}
                        right={0}
                        bottom={0}
                        pointerEvents="none"
                        overflow="hidden"
                        display="flex"
                        alignItems="center"
                        pl={6}
                        color="gray.100"
                        fontSize={{ base: "lg", md: "xl", lg: "2xl" }}
                        fontWeight="bold"
                      >
                        <AnimatePresence
                          mode="wait"
                          onExitComplete={handleExitComplete}
                        >
                          {!isExiting && (
                            <TypingSuggestion
                              key={currentIndex}
                              text={suggestions[currentIndex]}
                              onFinishedTyping={handleFinishedTyping}
                            />
                          )}
                          <Box
                            as="span"
                            ml={0} // Optional: adds a small margin to the left of the cursor
                            animation={`${blinkAnimation} 1s step-start infinite`}
                            mb={1}
                          >
                            |
                          </Box>
                        </AnimatePresence>
                      </Box>
                    )}

                    <InputRightElement width="auto" pr={1}>
                      <Button
                        type="submit"
                        h={{ base: "44px", md: "54px", lg: "72px", xl: "96px" }}
                        fontSize={{ base: "md", md: "lg", lg: "xl", xl: "1xl" }}
                        bg="white"
                        color="black"
                        borderRadius="sm"
                        // _hover={{ bg: "gray.200" }}
                        // transition="transform 0.3s"
                        // _hover={{
                        //   transform: "scale(1.1)",
                        //   // border: "2px solid #FFC719",
                        //   // borderColor: "#FFC719",
                        //   bg: "white",
                        // }}
                        onClick={(e) => handleSubmit(e)}
                        m={2}
                        mt={{ base: 5, md: 2, lg: 2, xl: 2 }}
                        mr={1}
                      >
                        <Box display={{ base: "none", md: "inline" }} mr={2}>
                          <Translate text="Get started - it's free" />
                        </Box>
                        <FaLocationArrow />
                      </Button>
                    </InputRightElement>
                  </InputGroup>
                </Box>
              </Flex>
            </Box>
          </Box>
        </Box>

        <VideoSection />
        {/* Partners Section */}
        <Box
          p={{ base: 5, md: 6, lg: 6, xl: 6 }}
          pb={{ base: 0, md: 0, lg: 0, xl: 0 }}
          pl={{ base: 5, md: 30, lg: 30, xl: 30 }}
          pr={{ base: 5, md: 30, lg: 30, xl: 30 }}
          ref={partnersRef}
          bg="#F8F9FA"
          textAlign="center"
          maxW="100%"
          overflow="hidden"
          borderBottom={{ base: "1px solid", md: "none" }}
          borderBottomColor={{ base: "gray.200", md: "none" }}
          boxShadow="lg"
        >
          <Heading as="h2" size="md" fontFamily="Poppins, sans-serif" mb={2}>
            <Translate text="Our Co-Pilots in Global Adventures" />
          </Heading>
          <Box
            maxW={{ base: "80vw", md: "80vw", lg: "82vw", xl: "65vw" }}
            mx={{ base: "auto", md: "auto", lg: "auto", xl: "auto" }}
            overflow="hidden"
          >
            <SlickSlider
              dots={true}
              infinite={true}
              speed={500}
              slidesToShow={5} // Number of images to show in a row
              slidesToScroll={1}
              autoplay={true}
              autoplaySpeed={2000}
              responsive={[
                {
                  breakpoint: 1024,
                  settings: {
                    slidesToShow: 4, // Show 4 logos on medium screens
                    slidesToScroll: 1,
                  },
                },
                {
                  breakpoint: 768,
                  settings: {
                    slidesToShow: 3, // Show 3 logos on smaller screens
                    slidesToScroll: 1,
                  },
                },
                {
                  breakpoint: 480,
                  settings: {
                    slidesToShow: 2, // Show 2 logos on very small screens
                    slidesToScroll: 1,
                  },
                },
              ]}
            >
              <Box p={0} textAlign="center">
                <Image
                  src={HomeToGo}
                  alt="Partner logo"
                  boxSize={{
                    base: "70px",
                    md: "100px",
                    lg: "100px",
                    xl: "100px",
                  }}
                  objectFit="contain"
                  mx={{ base: "2", md: "auto", lg: "auto", xl: "auto" }}
                />
              </Box>
              <Box p={0} textAlign="center">
                <Image
                  src={Viaiator}
                  alt="Partner logo"
                  boxSize={{
                    base: "70px",
                    md: "100px",
                    lg: "100px",
                    xl: "100px",
                  }}
                  objectFit="contain"
                  mx={{ base: "2", md: "auto", lg: "auto", xl: "auto" }}
                />
              </Box>
              <Box p={0} textAlign="center">
                <Image
                  src={Aviasales}
                  alt="Partner logo"
                  boxSize={{
                    base: "70px",
                    md: "100px",
                    lg: "100px",
                    xl: "100px",
                  }}
                  objectFit="contain"
                  mx={{ base: "2", md: "auto", lg: "auto", xl: "auto" }}
                />
              </Box>
              <Box p={0} textAlign="center">
                <Image
                  src={Outdoorsy}
                  alt="Partner logo"
                  boxSize={{
                    base: "70px",
                    md: "100px",
                    lg: "100px",
                    xl: "100px",
                  }}
                  objectFit="contain"
                  mx={{ base: "2", md: "auto", lg: "auto", xl: "auto" }}
                />
              </Box>
              <Box p={0} textAlign="center">
                <Image
                  src={SkyScanner}
                  alt="Partner logo"
                  boxSize={{
                    base: "70px",
                    md: "100px",
                    lg: "100px",
                    xl: "100px",
                  }}
                  objectFit="contain"
                  mx={{ base: "2", md: "auto", lg: "auto", xl: "auto" }}
                />
              </Box>
               <Box p={0} textAlign="center">
                <Image
                  src={expedia}
                  alt="Partner logo"
                  boxSize={{
                    base: "70px",
                    md: "100px",
                    lg: "100px",
                    xl: "100px",
                  }}
                  objectFit="contain"
                  mx={{ base: "2", md: "auto", lg: "auto", xl: "auto" }}
                />
              </Box>
            </SlickSlider>
          </Box>
        </Box>

        <TripSection />

        {/* Adventure Section */}
        <Box
          pb={{ base: 5, md: 8, lg: "52px", xl: 14 }}
          pt={{ base: 8, md: 5, lg: 10, xl: 14 }}
          pl={{ base: 5, md: 8, lg: 60, xl: 80 }}
          pr={{ base: 5, md: 8, lg: 60, xl: 80 }}
          h={{ base: "100%", md: "auto" }}
          textAlign="center"
          bg={colorMode === "light" ? "blackAlpha.50" : "blackAlpha.50"}
        >
          <Heading
            as="h2"
            size={{ base: "30px", md: "lg", lg: "xl", xl: "2xl" }}
            fontFamily="Poppins, sans-serif"
            mb={2}
          >
            <Translate text="Start Planning Your Next Adventure" />
          </Heading>
          <Text
            mb={{ base: 4, md: 8, lg: 8, xl: 8 }}
            fontSize={{ base: "sm", md: "md", lg: "md", xl: "20px" }}
          >
            <Translate text="Instantly Customized Travel Plans for Your Outdoor Adventures" />
          </Text>
          <Box
            position="relative"
            maxW={{ base: "100%", md: "80%", lg: "100%", xl: "75%" }}
            h={{ base: "auto", md: "70vh", lg: "70vh", xl: "60vh" }}
            mx="auto" // centers the image container horizontally
            mb={{ base: 48, md: 0 }}
          >
            <Image
              src={adventure}
              alt="Adventure image"
              borderRadius="xl"
              objectFit="cover"
              width="100%"
              height="100%"
            />
            <Box
              position={{ base: "absolute", md: "absolute" }}
              top={{ base: "70%", md: "50%", lg: "50%", xl: "50%" }}
              right={{ base: "+10%", md: "-25%", lg: "-15%", xl: "-20%" }}
              transform={{ base: "unset", md: "translateY(-50%)" }}
              bg="white"
              borderRadius="xl"
              p={{ base: 4, md: 4, lg: 6, xl: 6 }}
              boxShadow="lg"
              h={{ base: "auto", md: "auto" }}
              maxW={{ base: "80%", md: "50%", lg: "35%", xl: "40%" }}
              mt={{ base: 2, md: 0 }}
              mb={{ base: 0, md: 0 }}
              textAlign="left"
              display="flex"
              flexDirection="column"
              justifyContent="space-between"
            >
              <Text
                fontSize={{ base: "xs", md: "sm", lg: "sm", xl: "md" }}
                color="gray.500"
                mb={{ base: 0, md: 2 }}
              >
                <Translate text="Family Adventure" />
              </Text>
              <Box mb={{ base: 0, md: 2 }}>
                <Heading
                  as="h3"
                  fontSize={{ base: "1xl", md: "2x1", lg: "6x1", xl: "4xl" }}
                  fontFamily="Poppins, sans-serif"
                >
                  Route 66
                </Heading>
                <Text
                  mt={{ base: 0, md: 2 }}
                  fontSize={{ base: "xs", md: "sm", lg: "sm", xl: "md" }}
                >
                  <Translate
                    text="Embark on Route 66 with your family for an unforgettable RV
                journey. Discover historic landmarks, scenic byways, and
                family-friendly attractions across this iconic American road
                trip."
                  />
                </Text>
              </Box>
              <Button
                // mt={4}
                bg="black"
                color="white"
                size={{ base: "xs", md: "sm", lg: "md", xl: "lg" }}
                alignSelf="flex-start"
                // as={RouterLink}
                // to="/ai-travel-planner"
                onClick={() => handleLinkClick("Plan a road trip on route 66")}
                pt={2}
                pb={2}
                transition="transform 0.3s"
                _hover={{
                  transform: "scale(1.1)",
                  // border: "2px solid #FFC719",
                  // borderColor: "#FFC719",
                  // color: "black",
                  // bg: "white"
                }}
              >
                <Translate text="Plan a trip" />
              </Button>
            </Box>
          </Box>
        </Box>

        {/* FAQ Section */}
        <Box
          p={{ base: 8, md: 8, lg: 8, xl: 8 }}
          pl={{ base: 8, md: 8, lg: 60, xl: 60 }}
          pr={{ base: 8, md: 8, lg: 40, xl: 60 }}
        >
          <Flex
            direction={{ base: "column", md: "row" }}
            alignItems="right"
            w="100%"
          >
            <Box flex="2">
              <Heading
                as="h2"
                size={{ base: "30px", md: "xl", lg: "xl" }}
                fontFamily="Poppins, sans-serif"
                mb={4}
                ml={{ base: "4", md: "0", lg: "0", xl: 80 }}
                textAlign={{ base: "left", md: "left", lg: "left", xl: "left" }}
              >
                <Translate text="FAQs" />
              </Heading>
            </Box>
            <Box flex="2" w={{ base: "100%", md: "50%", lg: "50%", xl: "50%" }}>
              <Accordion
                allowToggle
                w={{ base: "100%", md: "100%", lg: "100%", xl: "70%" }}
              >
                <AccordionItem>
                  <AccordionButton
                    p={4}
                    fontSize={{ base: "sm", md: "md", lg: "md" }}
                    _hover="none"
                    fontWeight="bold"
                  >
                    <Box flex="1" textAlign="left">
                      <Translate text="What is NOMAD360?" />
                    </Box>
                    <AccordionIcon />
                  </AccordionButton>
                  <AccordionPanel
                    pb={4}
                    fontSize={{ base: "sm", md: "md", lg: "md" }}
                  >
                    <Translate
                      text="NOMAD360 creates personalized travel plans in seconds,
                  tailored to your outdoor lifestyle. Stress-free planning and
                  epic adventures await!"
                    />
                  </AccordionPanel>
                </AccordionItem>

                <AccordionItem>
                  <AccordionButton
                    p={4}
                    fontSize={{ base: "sm", md: "md", lg: "md" }}
                    _hover="none"
                    fontWeight="bold"
                  >
                    <Box flex="1" textAlign="left">
                      <Translate text="Who can use NOMAD360?" />
                    </Box>
                    <AccordionIcon />
                  </AccordionButton>
                  <AccordionPanel
                    pb={4}
                    fontSize={{ base: "sm", md: "md", lg: "md" }}
                  >
                    <Translate
                      text="NOMAD360 is an AI-powered travel planner designed for
                  outdoor and adventure travelers. It creates personalized
                  travel itineraries for hikers, campers, backpackers, families,
                  digital nomads, photographers, wildlife enthusiast,
                  overlanders, and much more."
                    />
                  </AccordionPanel>
                </AccordionItem>

                <AccordionItem>
                  <AccordionButton
                    p={4}
                    fontSize={{ base: "sm", md: "md", lg: "md" }}
                    _hover="none"
                    fontWeight="bold"
                  >
                    <Box flex="1" textAlign="left">
                      <Translate text="Is NOMAD360 free to use?" />
                    </Box>
                    <AccordionIcon />
                  </AccordionButton>
                  <AccordionPanel
                    pb={4}
                    fontSize={{ base: "sm", md: "md", lg: "md" }}
                  >
                    <Translate text="Yes, NOMAD360 is completely free! However, contributions are greatly appreciated to help maintain the platform and support our mission to a brighter, more sustainable future for all by offsetting CO2 emissions." />
                  </AccordionPanel>
                </AccordionItem>

                <AccordionItem>
                  <AccordionButton
                    p={4}
                    fontSize={{ base: "sm", md: "md", lg: "md" }}
                    _hover="none"
                    fontWeight="bold"
                  >
                    <Box flex="1" textAlign="left">
                      <Translate text="How do I adjust my NOMAD360' itinerary?" />
                    </Box>
                    <AccordionIcon />
                  </AccordionButton>
                  <AccordionPanel
                    pb={4}
                    fontSize={{ base: "sm", md: "md", lg: "md" }}
                  >
                    <Translate
                      text={`You can easily update your personalized NOMAD360 travel plans by clicking the edit icon in the Bucket List section. Additionally, when "Ongoing Trip" is toggled on in the Bucket List, you can view and modify your plans directly from the ongoing trip section.`}
                    />
                  </AccordionPanel>
                </AccordionItem>

                <AccordionItem>
                  <AccordionButton
                    p={4}
                    fontSize={{ base: "sm", md: "md", lg: "md" }}
                    _hover="none"
                    fontWeight="bold"
                  >
                    <Box flex="1" textAlign="left">
                      <Translate text="Can I set a budget limit in NOMAD360 for my trip?" />
                    </Box>
                    <AccordionIcon />
                  </AccordionButton>
                  <AccordionPanel
                    pb={4}
                    fontSize={{ base: "sm", md: "md", lg: "md" }}
                  >
                    <Translate
                      text="Yes, NOMAD360 offers a comprehensive budget planner that
                  allows you to set your financial preferences and supports
                  multiple currencies. This tool helps you track your expenses
                  while traveling, ensuring you stay on top of your spending."
                    />
                  </AccordionPanel>
                </AccordionItem>

                <AccordionItem>
                  <AccordionButton
                    p={4}
                    fontSize={{ base: "sm", md: "md", lg: "md" }}
                    _hover="none"
                    fontWeight="bold"
                  >
                    <Box flex="1" textAlign="left">
                      <Translate text="Can I access NOMAD360 offline?" />
                    </Box>
                    <AccordionIcon />
                  </AccordionButton>
                  <AccordionPanel
                    pb={4}
                    fontSize={{ base: "sm", md: "md", lg: "md" }}
                  >
                    <Translate
                      text="Yes, you can access NOMAD360 offline! By downloading your
                  itinerary and essential information before your trip, you'll
                  be well-prepared for areas without internet connectivity. This
                  allows you to enjoy your adventure worry-free, even when
                  you're off the grid."
                    />
                  </AccordionPanel>
                </AccordionItem>

                <AccordionItem>
                  <AccordionButton
                    p={4}
                    fontSize={{ base: "sm", md: "md", lg: "md" }}
                    _hover="none"
                    fontWeight="bold"
                  >
                    <Box flex="1" textAlign="left">
                      <Translate text="Where can I receive support for using NOMAD360?" />
                    </Box>
                    <AccordionIcon />
                  </AccordionButton>
                  <AccordionPanel
                    pb={4}
                    fontSize={{ base: "sm", md: "md", lg: "md" }}
                  >
                    <Translate
                      text={`If you have any specific questions or comments, go to "Report a Problem" in your account and submit your issue. Our team will get back to you as
                                               soon as possible. We’re here to help you make the most of your
                                               travel experience!`}
                    />
                  </AccordionPanel>
                </AccordionItem>
              </Accordion>
            </Box>
          </Flex>
        </Box>

        <JoinCommunitySection />
        {/* Footer Section */}
        <Footer />

        {/* {showCookiePopup && (
          <CookieConsentPopup onAccept={handleCookieAccept} />
        )} */}
        <NewsletterSubscriptionPopup
          isOpen={isNewsletterOpen}
          onClose={handleNewsletterClose}
        />
        {/* <SurveyPopup
          isOpen={isSurveyOpen}
          onClose={handleSurveyPopupClose} // Use the renamed function here
        /> */}
        <BackToTopButton />
      </Box>
    </>
  );
};

export default HomePage;
